import { ListSubheader } from '@mui/material';
import { useAuth } from '@/shared/auth/useAuth';
import { env } from '@/env';
import { NavItem } from './NavItem';
import { NavGroup } from './NavGroup';
import type { NavEntryConfig, NavItemConfig } from './navConfig';

/** A live badge to render on a specific item, resolved by item `id`. */
export interface NavItemBadge {
  readonly content: number;
  readonly label: string;
}

export interface NavSectionProps {
  readonly label: string;
  readonly items: readonly NavEntryConfig[];
  /** Drawer mini-variant — icon rail, no room for a section label. */
  readonly collapsed?: boolean;
  /** Resolve a live badge (e.g. the Leads unread count) for an item by id. */
  readonly getBadge?: (itemId: string) => NavItemBadge | undefined;
}

const renderItem = (
  item: NavItemConfig,
  collapsed: boolean,
  getBadge: NavSectionProps['getBadge'],
): JSX.Element => {
  const badge = getBadge?.(item.id);
  const Icon = item.icon;
  return (
    <NavItem
      key={item.id}
      to={item.to}
      label={item.label}
      icon={<Icon fontSize="small" />}
      requiresRole={item.requiresRole}
      end={item.end}
      collapsed={collapsed}
      badgeContent={badge?.content}
      badgeLabel={badge?.label}
    />
  );
};

/**
 * Static (non-collapsible) sidebar section: a label header followed by its
 * flat list of entries — plain `NavItem` links, or a nested collapsible
 * `NavGroup` (e.g. "Marketing" wrapping Campaigns + Leads inside Records).
 * The section itself has no expand/collapse chrome of its own — only a
 * group entry does.
 *
 * Badge rendering is delegated entirely to `NavItem`'s own `badgeContent`/
 * `badgeLabel` props (trailing pill when expanded, corner dot when
 * collapsed) for plain items — that treatment is already built and tested
 * on `NavItem` itself. For a group, any of its children's badges bubble
 * onto the GROUP's own header via `NavGroup`'s `badgeContent`, so a nested
 * badge isn't lost when the group is collapsed.
 *
 * Role-gated items are filtered here (not just inside `NavItem`) so the
 * section — and each group — can tell whether it has anything left to
 * show: a section whose every entry resolves to hidden renders no label at
 * all, and a group whose every child is hidden is dropped entirely rather
 * than rendering an empty toggle. This rule is generic (not special-cased
 * to Setup/Admin or Marketing) so it stays correct if any other
 * section/group gains a role-gated item later.
 */
export const NavSection = ({
  label,
  items,
  collapsed = false,
  getBadge,
}: NavSectionProps): JSX.Element | null => {
  const { hasRole } = useAuth();

  // `requiresEnv` is a visibility hint only (see NavItemConfig's doc comment)
  // — applied in BOTH branches below (top-level and group-child) so an
  // env-gated item nested inside a group can't silently leak through
  // ungated the way it would if only one branch checked it.
  const isItemVisible = (item: NavItemConfig): boolean =>
    (!item.requiresRole || hasRole(item.requiresRole)) &&
    (!item.requiresEnv || item.requiresEnv === env.appEnv);

  const visibleEntries = items.reduce<NavEntryConfig[]>((acc, entry) => {
    if (entry.type === 'group') {
      const visibleChildren = entry.children.filter(isItemVisible);
      if (visibleChildren.length > 0) {
        acc.push({ ...entry, children: visibleChildren });
      }
      return acc;
    }
    if (isItemVisible(entry)) {
      acc.push(entry);
    }
    return acc;
  }, []);

  if (visibleEntries.length === 0) {
    return null;
  }

  return (
    <>
      {!collapsed ? (
        // `disableSticky` — MUI's ListSubheader defaults to `position: sticky`
        // (pinned to the top of the scrollable nav region), which combined
        // with this label's transparent background let scrolled item text
        // bleed through the "stuck" label instead of being cleanly covered.
        // This is a short, static 4-section nav (well under a dozen total
        // items) — sticky "which section am I in" headers earn their keep on
        // long, alphabetically-indexed lists, not here, so the fix removes
        // stickiness entirely rather than opaquing the background.
        <ListSubheader
          component="div"
          disableSticky
          sx={{
            lineHeight: '32px',
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {label}
        </ListSubheader>
      ) : null}
      {visibleEntries.map((entry) => {
        if (entry.type === 'group') {
          const GroupIcon = entry.icon;
          // Only Leads carries a live badge today; summed in case a future
          // group gains a second badged child.
          const groupBadgeContent = entry.children.reduce(
            (sum, child) => sum + (getBadge?.(child.id)?.content ?? 0),
            0,
          );
          const groupBadgeLabel = entry.children
            .map((child) => getBadge?.(child.id)?.label)
            .find((l): l is string => Boolean(l));
          return (
            <NavGroup
              key={entry.id}
              label={entry.label}
              icon={<GroupIcon fontSize="small" />}
              collapsed={collapsed}
              persistKey={entry.id}
              badgeContent={groupBadgeContent}
              badgeLabel={groupBadgeLabel}
            >
              {entry.children.map((child) => renderItem(child, collapsed, getBadge))}
            </NavGroup>
          );
        }
        return renderItem(entry, collapsed, getBadge);
      })}
    </>
  );
};
